import { useNavigateToSearch } from "hooks/navigation";
import { useTenant } from "hooks/settings";
import { useTenantData } from "contexts/tenant";
import React, { createContext, use, useMemo, useState } from "react";
import {
  InventoryEstimate,
  SearchType,
  useCreateAndRunAutoRecipeMutation,
  useCreateInventoryMeasurementSetMutation,
  useGetInventoryEstimateAtTimestampQuery,
} from "src/store/api/generatedApi";
import {
  InventoryMeasurementFormData,
  InventoryMeasurementFormState,
} from "../types";
import { useIsBehindFeatureFlag } from "hooks/useFeatureFlag";

interface InventoryMeasurementContextProps {
  inventoryEstimate: InventoryEstimate | undefined;
  inventoryMeasurementFormData: InventoryMeasurementFormData;
  adjustQuantity: (
    materialName: string,
    adjustedQuantity: number | null | undefined
  ) => void;
  canSubmit: boolean;
  handleSubmit: (recipeType: SearchType) => Promise<void>;
  inventoryMeasurementFormState: InventoryMeasurementFormState;
}

const InventoryMeasurementContext = createContext<
  InventoryMeasurementContextProps | undefined
>(undefined);

export const InventoryMeasurementProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const tenantName = useTenant();
  const tenantData = useTenantData();
  const isInventoryMeasurementWithLocationsEnabled = useIsBehindFeatureFlag(
    "inventory_measurement_with_locations"
  );

  const nowIso = useMemo(() => new Date().toISOString(), []);

  const { data: inventoryEstimate } = useGetInventoryEstimateAtTimestampQuery(
    {
      tenantName,
      materialMetadataSetId: "default",
      materialPhysicsSetId: "default",
      timestamp: nowIso,
    },
    { skip: isInventoryMeasurementWithLocationsEnabled }
  );

  const [inventoryMeasurementFormState, setInventoryMeasurementFormState] =
    useState(InventoryMeasurementFormState.Pending);

  const [updateInventoryMeasurement] =
    useCreateInventoryMeasurementSetMutation();

  const [createAndRunAutoRecipe] = useCreateAndRunAutoRecipeMutation();
  const navigateToSearch = useNavigateToSearch();

  const [inventoryMeasurementFormData, setInventoryMeasurementFormData] =
    useState<InventoryMeasurementFormData>({ items: [] });

  // eslint-disable-next-line @eslint-react/exhaustive-deps -- adjustQuantity is intentionally not wrapped in useCallback; it only calls setInventoryMeasurementFormData which is stable
  const adjustQuantity = (
    materialName: string,
    parsedQuantity: number | null | undefined
  ) => {
    setInventoryMeasurementFormData((prev) => {
      const itemIndex = prev.items.findIndex(
        (item) => item.material_name === materialName
      );
      if (itemIndex !== -1) {
        if (parsedQuantity === undefined || parsedQuantity === null) {
          return {
            ...prev,
            items: prev.items.filter(
              (item) => item.material_name !== materialName
            ),
          };
        }
        return {
          ...prev,
          items: prev.items.map((item, idx) =>
            idx === itemIndex
              ? { ...item, adjusted_quantity: parsedQuantity }
              : item
          ),
        };
      } else {
        if (parsedQuantity === undefined || parsedQuantity === null) {
          return prev;
        }
        return {
          ...prev,
          items: [
            ...prev.items,
            {
              material_name: materialName,
              adjusted_quantity: parsedQuantity,
            },
          ],
        };
      }
    });
  };

  const hasEdits = inventoryMeasurementFormData.items.length > 0;

  const canSubmit = useMemo(() => {
    return !!inventoryEstimate;
  }, [inventoryEstimate]);

  // eslint-disable-next-line @eslint-react/exhaustive-deps -- handleSubmit is intentionally not wrapped in useCallback; it captures current form state via closure
  const handleSubmit = async (recipeType: SearchType) => {
    setInventoryMeasurementFormState(InventoryMeasurementFormState.Submitting);
    if (hasEdits && inventoryEstimate) {
      const measurementMap = new Map(
        inventoryMeasurementFormData.items.map((item) => [
          item.material_name,
          item.adjusted_quantity,
        ])
      );

      await updateInventoryMeasurement({
        tenantName,
        inventoryMeasurementSetCreate: {
          items: inventoryEstimate.items.map((item) => ({
            material_name: item.material_name,
            quantity: measurementMap.has(item.material_name)
              ? (measurementMap.get(item.material_name) ?? 0)
              : (item.quantity ?? 0),
            quantity_scrap_bay: 0,
            quantity_yard: 0,
            quantity_rail_car: 0,
            quantity_scrap_bay_estimate: item.quantity ?? 0,
          })),
        },
      }).unwrap();
    }
    const additionalRecipeTypes =
      tenantData.inventory_form_recipe_fanout.includes(recipeType)
        ? tenantData.inventory_form_recipe_fanout.filter(
            (type) => type !== recipeType
          )
        : [];
    const [primarySearch] = await Promise.all([
      createAndRunAutoRecipe({
        tenantName,
        autoRecipeCreate: { auto_recipe_type: recipeType },
      }).unwrap(),
      ...additionalRecipeTypes.map((type) =>
        createAndRunAutoRecipe({
          tenantName,
          autoRecipeCreate: { auto_recipe_type: type },
        })
          .unwrap()
          .catch(() => null)
      ),
    ]);
    setInventoryMeasurementFormState(InventoryMeasurementFormState.Submitted);
    navigateToSearch(primarySearch.id);
  };

  const value = useMemo(
    () => ({
      inventoryEstimate,
      inventoryMeasurementFormData,
      adjustQuantity,
      handleSubmit,
      inventoryMeasurementFormState,
      canSubmit,
    }),
    [
      inventoryEstimate,
      inventoryMeasurementFormData,
      adjustQuantity,
      handleSubmit,
      inventoryMeasurementFormState,
      canSubmit,
    ]
  );

  return (
    <InventoryMeasurementContext value={value}>
      {children}
    </InventoryMeasurementContext>
  );
};

export const useInventoryMeasurement = () => {
  const context = use(InventoryMeasurementContext);
  if (!context) {
    throw new Error(
      "useInventoryMeasurement must be used within a InventoryMeasurementProvider"
    );
  }
  return context;
};
